import { useCallback, useEffect, useState } from "react";
import { trackProductEvent, type ProductBaseProperties } from "../analytics/product";
import { pushSubscriptionRepository } from "../repositories/pushSubscriptionRepository";

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

export type PushPermissionState =
  | "granted"
  | "denied"
  | "default"
  | "unsupported";

export type PushAnalyticsContext = ProductBaseProperties;

export type SubscribeOptions = {
  requestPermission?: boolean;
  analytics?: PushAnalyticsContext;
};

const DEFAULT_ANALYTICS: PushAnalyticsContext = {
  pageKey: "settings",
  routePath: "/settings",
  sourcePage: "Settings",
  productArea: "settings",
};

const isPushSupported = () =>
  "Notification" in window &&
  "serviceWorker" in navigator &&
  "PushManager" in window;

export const usePushNotifications = () => {
  const [permission, setPermission] = useState<PushPermissionState>("default");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const refreshPermission = useCallback(() => {
    if (!isPushSupported()) {
      setPermission("unsupported");
      return;
    }

    setPermission(Notification.permission as PushPermissionState);
  }, []);

  const refreshSubscriptionState = useCallback(async () => {
    if (!("serviceWorker" in navigator)) {
      setIsSubscribed(false);
      return;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(Boolean(subscription));
    } catch {
      setIsSubscribed(false);
    }
  }, []);

  useEffect(() => {
    refreshPermission();
    void refreshSubscriptionState();
  }, [refreshPermission, refreshSubscriptionState]);

  const registerSubscription = useCallback(
    async (analytics: PushAnalyticsContext) => {
      const publicKey = await pushSubscriptionRepository.getVapidPublicKey();
      if (!publicKey) {
        return false;
      }

      const registration = await navigator.serviceWorker.ready;
      const existingSubscription =
        await registration.pushManager.getSubscription();

      // Reuse an existing browser subscription when present and upsert it for the
      // current authenticated user on the backend.
      let subscription = existingSubscription;
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      const json = subscription.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        return false;
      }

      await pushSubscriptionRepository.subscribe({
        endpoint: json.endpoint,
        keys: {
          p256dh: json.keys.p256dh,
          auth: json.keys.auth,
        },
        userAgent: navigator.userAgent,
      });

      setIsSubscribed(true);
      trackProductEvent("push_subscribed", analytics, undefined, "notifications");
      return true;
    },
    [],
  );

  const subscribe = useCallback(
    async (options?: SubscribeOptions) => {
      if (!isPushSupported()) {
        setPermission("unsupported");
        return false;
      }

      const requestPermission = options?.requestPermission ?? true;
      const analytics = options?.analytics ?? DEFAULT_ANALYTICS;

      setIsLoading(true);
      try {
        if (requestPermission) {
          trackProductEvent(
            "push_permission_requested",
            analytics,
            undefined,
            "notifications",
          );

          const permissionResult = await Notification.requestPermission();
          setPermission(permissionResult as PushPermissionState);

          if (permissionResult !== "granted") {
            return false;
          }
        } else if (Notification.permission !== "granted") {
          return false;
        }

        const registered = await registerSubscription(analytics);
        return registered;
      } catch (error) {
        console.error("[usePushNotifications] subscribe failed:", error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [registerSubscription],
  );

  const ensureSubscribed = useCallback(
    (analytics?: PushAnalyticsContext) =>
      subscribe({ requestPermission: false, analytics }),
    [subscribe],
  );

  const unsubscribe = useCallback(async () => {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await pushSubscriptionRepository.unsubscribe(subscription.endpoint);
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
    } catch (error) {
      console.error("[usePushNotifications] unsubscribe failed:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    permission,
    isSubscribed,
    isLoading,
    subscribe,
    ensureSubscribed,
    unsubscribe,
    refreshPermission,
    refreshSubscriptionState,
  };
};
